import {useEffect} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {socket} from '@/socket'
import {QUERY_KEYS} from '@/queryClient'
import {ExportJob, exportStatusRank} from '@/features/exports/types'
import {HttpGetActivityReportHistoryResponse} from '@/features/accesses/activity-report/http/activityReport.http'
import {invalidateActivityReportExportAvailability} from '@/features/accesses/activity-report/queries/invalidateActivityReportExportAvailability'

/**
 * Keeps the embedded export-job state of activity-report history rows live (FR-021).
 *
 * The `users:{userId}` room is already joined by `useSocketNotifications`, so this hook only
 * adds/removes the `export:status_changed` listener. On each event it patches every cached
 * ACTIVITY_REPORTS list, replacing the matching row's `exportJob` (matched by job id) — guarding
 * against stale/out-of-order transitions with the shared monotonic status rank. React Query
 * refetch/invalidation remains the on-demand fallback for state we did not receive via socket.
 */
export const useActivityReportExportSocket = () => {
    const queryClient = useQueryClient()

    useEffect(() => {
        const handler = (raw: unknown) => {
            const parsed = ExportJob.safeParse(raw)
            if (!parsed.success) return
            const job = parsed.data

            const lists = queryClient.getQueriesData<HttpGetActivityReportHistoryResponse>({
                queryKey: [QUERY_KEYS.ACTIVITY_REPORTS]
            })
            let hasChangedActivityReport = false

            for (const [key, data] of lists) {
                if (!data) continue
                let changed = false
                const items = data.items.map(item => {
                    if (item.exportJob?.id !== job.id) return item
                    // Ignore stale/out-of-order transitions (e.g. a late `processing` after `ready`).
                    if (exportStatusRank(job.status) < exportStatusRank(item.exportJob.status)) return item
                    changed = true
                    return {
                        ...item,
                        exportJob: {
                            ...item.exportJob,
                            status: job.status,
                            downloadUrl: job.downloadUrl ?? item.exportJob.downloadUrl,
                            fileName: job.fileName ?? item.exportJob.fileName
                        }
                    }
                })
                if (changed) {
                    hasChangedActivityReport = true
                    queryClient.setQueryData(key, {...data, items})
                }
            }

            if (hasChangedActivityReport) invalidateActivityReportExportAvailability(queryClient)
        }

        socket.on('export:status_changed', handler)
        return () => {
            socket.off('export:status_changed', handler)
        }
    }, [queryClient])
}
